import React, { useEffect } from 'react';
import { Form, Input, Button, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useStoreActions, useStoreState } from '../store';
import { Link, useHistory } from 'react-router-dom';
import { UnauthenticatedLayout } from '../layout/UnauthenticatedLayout';
import { LocalizedText } from '../components/i18n/LocalizedText';
import { createUseStyles } from 'react-jss';
import { useSession } from '~/hooks/useSession';
import { Flex } from '~/components/Flex';
import { PrivacyConsentBanner } from '~/components/account/PrivacyConsentBanner';
import { useSessionStorage } from 'usehooks-ts';

const useStyles = createUseStyles({
  card: {
    '& li': {
      width: '100% !important',
    },
  },
});

export const LoginForm = () => {
  const [returnUrl, setReturnUrl] = useSessionStorage('returnUrl', '');
  const authenticateUser = useStoreActions((actions) => actions.session.authenticateUser);
  const i18n = useStoreState((state) => state.i18n.activeResources);
  const classes = useStyles();
  const history = useHistory();
  const { sessionPartnerUrl, user } = useSession();

  const onFinish = (values: any) => {
    authenticateUser(values).then((res) => {
      // On failure, the thunk returns undefined and the user already saw a notification.
      if (!res) return;
      if (res.resetToken) {
        history.push(`/account/${res.resetToken}/change-password`);
      } else if (window.location.pathname === '/login' || window.location.pathname === '/') {
        const { sessionPartnerUrl } = res.user;
        const path = returnUrl || `/${sessionPartnerUrl}/`;
        history.push(path);
      }
    });
  };

  useEffect(() => {
    if (user && returnUrl) {
      setReturnUrl('');
      history.push(returnUrl);
    }
  }, [user, returnUrl, setReturnUrl, history]);

  if (user) {
    return (
      <UnauthenticatedLayout
        className={classes.card}
        actions={[
          <Link to={`/${sessionPartnerUrl}/`} key="home">
            <Typography.Text>Go to Dashboard</Typography.Text>
          </Link>,
          <Link to="/logout" key="logout">
            <Typography.Text>Logout</Typography.Text>
          </Link>,
        ]}
      >
        <Flex direction="vertical">
          <Typography.Text strong>You are already logged in.</Typography.Text>
        </Flex>
      </UnauthenticatedLayout>
    );
  }

  return (
    <UnauthenticatedLayout
      className={classes.card}
      footer={<PrivacyConsentBanner />}
      actions={[
        <Link data-testid="forgot-password" to="/forgot-password" key="forgot-password">
          <LocalizedText token="common.forgotPassword" />
        </Link>,
        <Link to="/sso" key="sso">
          <LocalizedText token="common.sso" />
        </Link>,
      ]}
    >
      <Form onFinish={onFinish}>
        <Form.Item
          style={{ marginBottom: 10 }}
          name="email"
          normalize={(value: string) => value.trim()}
          rules={[{ required: true, message: <LocalizedText token="account.login.emailRequired" /> }]}
        >
          <Input prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} placeholder={i18n['account.email']} />
        </Form.Item>
        <Form.Item
          style={{ marginBottom: 10 }}
          name="password"
          rules={[{ required: true, message: <LocalizedText token="account.passwordRequired" /> }]}
        >
          <Input
            prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            type="password"
            placeholder={i18n['account.password']}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" className="login-form-button" style={{ width: '100%' }}>
            <LocalizedText token="common.login" />
          </Button>
        </Form.Item>
      </Form>
    </UnauthenticatedLayout>
  );
};

export default LoginForm;
